import React from "react";
import memoize from "memoize-one";
import * as d3 from "./assets/d3";
import { Ellipse } from "./Ellipse";
export const PolarGrid = ({ xScale, yScale, clipId }) => {
    const center = { x: xScale(0.0), y: yScale(0.0) };
    const t = `translate(0,0)`;
    const ellipses = computeEllipses(xScale, yScale);
    const radial = computeRadialLines(xScale, yScale);
    const midCircle = ellipses[Math.floor(ellipses.length / 2.0)];
    const ba = Math.abs((yScale(0) - yScale(1)) / (xScale(0) - xScale(1)));
    const midPoint = [
        xScale.domain().reduce((a, b) => a + b, 0) / 2.0,
        yScale.domain().reduce((a, b) => a + b, 0) / 2.0,
    ];
    const centerAngle = degrees(Math.atan2(midPoint[1], midPoint[0]));
    const centerPseudoAngle = angle0_360(computePseudoAngle(centerAngle, ba));
    return (React.createElement("g", { "data-testid": "polarGrid", className: "focusGrafos-polarGrid", transform: t, clipPath: `url(#${clipId})` },
        ellipses.map((ellipse, index) => (React.createElement(React.Fragment, { key: `ell_${index}` },
            React.createElement(Ellipse, { xScale: xScale, yScale: yScale, radius: ellipse.radius, radiusX: ellipse.rx, radiusY: ellipse.ry, centerX: center.x, centerY: center.y }),
            ")",
            React.createElement("g", { transform: `translate(${center.x},${center.y}),rotate(${-centerPseudoAngle})` },
                React.createElement("text", { x: Math.sqrt((ellipse.rx * Math.cos(radians(centerAngle))) ** 2 +
                        (ellipse.ry * Math.sin(radians(centerAngle))) ** 2), dy: ".35em", transform: centerPseudoAngle < 270 && centerPseudoAngle > 90
                        ? "rotate(180 " +
                            Math.sqrt((ellipse.rx * Math.cos(radians(centerAngle))) ** 2 +
                                (ellipse.ry * Math.sin(radians(centerAngle))) ** 2) +
                            " 0)"
                        : null, style: {
                        textAnchor: centerPseudoAngle < 270 && centerPseudoAngle > 90
                            ? "end"
                            : null,
                    } }, `${ellipse.radius}`))))),
        radial.map(({ ang, length, trueAng }, index) => (React.createElement("g", { key: `radial_${index}`, transform: `translate(${center.x},${center.y}),rotate(${-ang})` },
            React.createElement("line", { x2: length, style: { strokeWidth: 1, stroke: "#CCC", strokeDasharray: "4" } }),
            React.createElement("text", { x: Math.sqrt((midCircle.rx * Math.cos(radians(trueAng))) ** 2 +
                    (midCircle.ry * Math.sin(radians(trueAng))) ** 2), dy: ".35em", transform: trueAng < 270 && trueAng > 90
                    ? "rotate(180 " +
                        Math.sqrt((midCircle.rx * Math.cos(radians(trueAng))) ** 2 +
                            (midCircle.ry * Math.sin(radians(trueAng))) ** 2) +
                        " 0)"
                    : null, style: { textAnchor: trueAng < 270 && trueAng > 90 ? "end" : null } }, `${trueAng}°`))))));
};
const radians = (degrees) => {
    return (degrees / 180) * Math.PI;
};
const degrees = (radians) => {
    return (radians * 180) / Math.PI;
};
const distanceToCenter = (pos) => {
    const dx = pos[0];
    const dy = pos[1];
    return Math.sqrt(dx ** 2 + dy ** 2);
};
const pixelDistanceToCenter = (xScale, yScale) => (pos) => {
    const dx = xScale(pos[0]) - xScale(0);
    const dy = yScale(pos[1]) - yScale(0);
    return Math.sqrt(dx ** 2 + dy ** 2);
};
const domainCorners = (xDomain, yDomain) => {
    return xDomain.reduce((acc, x) => {
        yDomain.forEach((y) => acc.push([x, y]));
        return acc;
    }, []);
};
export const computeEllipses = memoize((xScale, yScale) => {
    const xDomain = xScale.domain();
    const yDomain = yScale.domain();
    const isCenterOutside = xDomain[0] * xDomain[1] > 0.0 || yDomain[0] * yDomain[1] > 0.0;
    const corners = domainCorners(xDomain, yDomain);
    const maxDistance = Math.max(...corners.map(distanceToCenter));
    const minDistance = isCenterOutside
        ? Math.min(...corners.map(distanceToCenter))
        : 0.0;
    const radii = d3
        .scaleLinear()
        .domain([minDistance, maxDistance])
        .nice(10)
        .ticks();
    const ref = { x: xScale(0.0), y: yScale(0.0) };
    return radii.map((radius) => ({
        radius,
        rx: Math.abs(xScale(radius) - ref.x),
        ry: Math.abs(yScale(radius) - ref.y),
    }));
});
const angle0_360 = (angle) => (angle + 360) % 360;
const getAnglesRange = (corners, isAtLeftQuadrant) => {
    const angles_180 = corners.map((pos) => degrees(Math.atan2(pos[1], pos[0])));
    const angles_0_360 = angles_180.map((angle) => (angle + 360) % 360);
    let minAngle;
    let maxAngle;
    if (isAtLeftQuadrant) {
        minAngle = Math.min(...angles_180);
        maxAngle = Math.max(...angles_180);
    }
    else {
        minAngle = Math.min(...angles_0_360);
        maxAngle = Math.max(...angles_0_360);
    }
    if (maxAngle - minAngle > 180) {
        [minAngle, maxAngle] = [maxAngle - 360, minAngle];
    }
    if (maxAngle - minAngle < 1) {
        return [(maxAngle + minAngle) / 2];
    }
    else {
        const maxAngleint = Math.floor(maxAngle);
        const minAngleint = Math.ceil(minAngle);
        const step = Math.floor((maxAngleint - minAngleint) / 6);
        if (step < 1) {
            return d3.range(minAngleint, maxAngleint);
        }
        else {
            return d3.range(minAngleint, maxAngleint, step);
        }
    }
};
const computePseudoAngle = (ang, ba) => {
    return ((Math.atan2(Math.sin(radians(ang)), Math.cos(radians(ang)) / ba) * 180) /
        Math.PI);
};
const computeRadialLines = memoize((xScale, yScale) => {
    const xDomain = xScale.domain();
    const yDomain = yScale.domain();
    const corners = domainCorners(xDomain, yDomain);
    const maxDistance = Math.max(...corners.map(pixelDistanceToCenter(xScale, yScale)));
    const ba = Math.abs((yScale(0) - yScale(1)) / (xScale(0) - xScale(1)));
    const isCenterOutside = xDomain[0] * xDomain[1] > 0.0 || yDomain[0] * yDomain[1] > 0.0;
    const isAtLeftQuadrant = xDomain[0] > 0.0 && xDomain[1] > 0.0;
    const angleSteps = isCenterOutside
        ? getAnglesRange(corners, isAtLeftQuadrant)
        : d3.range(0, 360, 30);
    const angles = angleSteps.map((ang, index) => ({
        ang: computePseudoAngle(ang, ba),
        trueAng: ang,
        length: maxDistance,
        index,
    }));
    return angles;
});
//# sourceMappingURL=PolarGrid.js.map